import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Grid, Typography, Button } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import ControlledFileUploadField from '../../../../components/controlledComponents/ControlledFileUploadField.component';
import {
  TFinanceDetails,
  TStatus,
  TPaymentMode,
  TTransactionType,
  TTransactionCategory,
  TTransactionCurrency,
  TFinance,
} from '../../../../../apis/finance/finance.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { localStorageGet } from 'src/utils/localStorage';
import {
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
  zOptionalYearFromInput,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import {
  useFinanceState,
  useFinanceActions,
} from '../../contexts/finance.context';
import { useFinanceService } from '../../../../../apis/finance/useFinanceService';
import dayjs, { Dayjs } from 'dayjs';

type FinanceFormData = {
  name: string;
  description: string;
  amount: number;
  currency: TTransactionCurrency;
  date: Dayjs | null;
  dueDate?: Dayjs | null;
  tags?: string;
  type: TTransactionType;
  category: TTransactionCategory;
  status: TStatus;
  paymentMode: TPaymentMode;
  paidAmount?: number;
  academicYear?: number;
  documents?: Array<{
    _id?: string;
    name: string;
    document_type: string;
    status: string;
    file_key: string;
    title?: string;
    description?: string;
    verificationDate?: string;
  }>;
};

const getFinanceFormSchema = (translateText: (key: string) => string) => {
  return z.object({
    name: z.string().min(1, translateText('common.fieldRequired')),
    description: z.string().min(1, translateText('common.fieldRequired')),
    amount: zNumberFromInput({
      min: 0.01,
      message:
        translateText('finance.form.amountMinError') ||
        'Amount must be greater than 0',
    }),
    currency: z.nativeEnum(TTransactionCurrency, {
      required_error: translateText('common.fieldRequired'),
    }),
    date: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    dueDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    tags: z.string().optional(),
    type: z.nativeEnum(TTransactionType, {
      required_error: translateText('common.fieldRequired'),
    }),
    category: z.nativeEnum(TTransactionCategory, {
      required_error: translateText('common.fieldRequired'),
    }),
    status: z.nativeEnum(TStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    paymentMode: z.nativeEnum(TPaymentMode, {
      required_error: translateText('common.fieldRequired'),
    }),
    paidAmount: zOptionalNumberFromInput(),
    academicYear: zOptionalYearFromInput({
      message:
        translateText('finance.form.academicYearInvalid') || undefined,
    }),
    documents: z.array(z.any()).optional(),
  });
};

const getCurrencyOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TTransactionCurrency.USD,
      value: translateText('finance.form.currencyUSD') || 'USD',
    },
    {
      key: TTransactionCurrency.EUR,
      value: translateText('finance.form.currencyEUR') || 'EUR',
    },
    {
      key: TTransactionCurrency.GBP,
      value: translateText('finance.form.currencyGBP') || 'GBP',
    },
    {
      key: TTransactionCurrency.INR,
      value: translateText('finance.form.currencyINR') || 'INR',
    },
  ];

const getTransactionTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TTransactionType.FEES,
      value: translateText('finance.list.filters.fees') || 'Fees',
    },
    {
      key: TTransactionType.INCOME,
      value: translateText('finance.list.filters.income') || 'Income',
    },
    {
      key: TTransactionType.EXPENSE,
      value: translateText('finance.list.filters.expense') || 'Expense',
    },
  ];

const getCategoryOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TTransactionCategory.FEES,
      value: translateText('finance.form.categoryFees') || 'Fees',
    },
    {
      key: TTransactionCategory.SALARY,
      value: translateText('finance.form.categorySalary') || 'Salary',
    },
    {
      key: TTransactionCategory.EXPENSE,
      value: translateText('finance.form.categoryExpense') || 'Expense',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TStatus.PAID,
      value: translateText('finance.form.statusPaid') || 'Paid',
    },
    {
      key: TStatus.PARTIAL,
      value: translateText('finance.form.statusPartial') || 'Partial',
    },
    {
      key: TStatus.UNPAID,
      value: translateText('finance.form.statusUnpaid') || 'Unpaid',
    },
    {
      key: TStatus.N_A,
      value: translateText('finance.form.statusNA') || 'N/A',
    },
  ];

const getPaymentModeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: TPaymentMode.CASH,
      value: translateText('finance.form.paymentModeCash') || 'Cash',
    },
    {
      key: TPaymentMode.BANK,
      value: translateText('finance.form.paymentModeBank') || 'Bank',
    },
    {
      key: TPaymentMode.CARD,
      value: translateText('finance.form.paymentModeCard') || 'Card',
    },
    {
      key: TPaymentMode.ONLINE,
      value: translateText('finance.form.paymentModeOnline') || 'Online',
    },
    {
      key: TPaymentMode.CHEQUE,
      value: translateText('finance.form.paymentModeCheque') || 'Cheque',
    },
    {
      key: TPaymentMode.BANK_TRANSFER,
      value:
        translateText('finance.form.paymentModeBankTransfer') || 'Bank Transfer',
    },
    {
      key: TPaymentMode.N_A,
      value: translateText('finance.form.paymentModeNA') || 'N/A',
    },
  ];

const AddEditFinancialRecordView: FC = () => {
  const { translateText } = useTranslation();
  const financeFormSchema = getFinanceFormSchema(translateText);
  const { financeAppState } = useFinanceState();
  const {
    setIsManageFinanceItemOpen,
    setSelectedFinanceItem,
    setIsCreateMode,
    setIsEditMode,
    setIsFinanceItemLoading,
    setIsFinanceItemError,
    setFinanceItemErrorMessage,
  } = useFinanceActions();
  const { createFinanceRecord, patchFinanceRecord } = useFinanceService();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const defaultValues: FinanceFormData = {
    name: '',
    description: '',
    amount: 0,
    currency: TTransactionCurrency.INR,
    date: dayjs(),
    dueDate: null,
    tags: '',
    type: TTransactionType.EXPENSE,
    category: TTransactionCategory.EXPENSE,
    status: TStatus.UNPAID,
    paymentMode: TPaymentMode.CASH,
    paidAmount: undefined,
    academicYear: undefined,
    documents: [],
  };

  const methods = useForm<FinanceFormData>({
    resolver: zodResolver(financeFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  useEffect(() => {
    if (
      financeAppState.selectedFinanceItem &&
      financeAppState.isManageFinanceItemOpen
    ) {
      const item = financeAppState.selectedFinanceItem;
      const basicDetails = item.basic_details;
      reset({
        name: basicDetails.description || '',
        description: basicDetails.description || '',
        amount: basicDetails.amount || 0,
        currency:
          (basicDetails.currency_code as TTransactionCurrency) ||
          TTransactionCurrency.INR,
        date: dayjs(),
        dueDate: basicDetails.due_date ? dayjs(basicDetails.due_date) : null,
        tags: '',
        type:
          (basicDetails.type as TTransactionType) || TTransactionType.EXPENSE,
        category: TTransactionCategory.EXPENSE,
        status: (basicDetails.status as TStatus) || TStatus.UNPAID,
        paymentMode:
          (basicDetails.payment_mode as TPaymentMode) || TPaymentMode.CASH,
        paidAmount: basicDetails.paid_amount,
        academicYear: basicDetails.academic_year,
        documents:
          item.documents?.map((doc) => ({
            _id: doc._id,
            name: doc.file_key,
            document_type: doc.document_type,
            status: doc.status,
            file_key: doc.file_key,
            title: doc.name,
          })) || [],
      });
    } else if (financeAppState.isManageFinanceItemOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    financeAppState.selectedFinanceItem,
    financeAppState.isManageFinanceItemOpen,
    reset,
  ]);

  const mapToTFinance = (
    data: FinanceFormData,
    existingItem?: TFinanceDetails
  ): TFinance => {
    const tagsArray = data.tags
      ? data.tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)
      : [];

    return {
      id: existingItem?.basic_details._id || '',
      amount: data.amount,
      currencyCode: data.currency,
      description: data.description,
      fine: existingItem?.basic_details.fine ?? 0,
      fineCurrencyCode:
        (existingItem?.basic_details
          .fine_currency_code as TTransactionCurrency) || data.currency || 'IND',
      instituteId: existingItem?.institute._id || loggedInUserId,
      isCredit: data.type === TTransactionType.INCOME,
      tags: tagsArray,
      type: data.type,
      category: data.category,
      academicYear: data.academicYear || new Date().getFullYear(),
      dueDate: data.dueDate ? data.dueDate.toDate() : new Date(),
      purpose: data.name,
      invoiceKey: '',
      createdBy: {
        id: loggedInUserId,
        name: userFromStorage
          ? `${userFromStorage.firstName} ${userFromStorage.lastName}`.trim()
          : '',
        userRole: userFromStorage?.roles?.[0] || '',
        createdAt: new Date(),
      },
      createdAt: existingItem?.action.created_at || new Date(),
    };
  };

  const onSubmitForm = async (data: FinanceFormData) => {
    try {
      setIsFinanceItemLoading(true);
      setIsFinanceItemError(false);
      setFinanceItemErrorMessage('');

      const financeData = mapToTFinance(
        data,
        financeAppState.selectedFinanceItem || undefined
      );

      if (financeAppState.isCreateMode) {
        await createFinanceRecord(financeData);
      } else if (financeAppState.isEditMode) {
        await patchFinanceRecord(financeData);
      }

      setIsManageFinanceItemOpen(false);
      setSelectedFinanceItem(null);
      setIsCreateMode(false);
      setIsEditMode(false);
    } catch (error: any) {
      setIsFinanceItemError(true);
      setFinanceItemErrorMessage(
        error?.response?.data?.message ||
        translateText('finance.form.saveError') ||
        'Failed to save financial record'
      );
    } finally {
      setIsFinanceItemLoading(false);
    }
  };

  const handleClose = () => {
    setIsManageFinanceItemOpen(false);
    setIsFinanceItemLoading(false);
    setIsFinanceItemError(false);
    setFinanceItemErrorMessage('');
  };

  return (
    <div data-testid="add-edit-financial-record-container">
      <StructuredModal
        isOpen={financeAppState.isManageFinanceItemOpen}
        onClose={handleClose}
        title={
          financeAppState.isEditMode
            ? translateText('finance.list.editTransaction') ||
            'Edit Transaction'
            : translateText('finance.list.addTransaction') || 'Add Transaction'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          financeAppState.isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={financeAppState.isFinanceItemLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          financeAppState.financeItemErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="finance-error-message"
            >
              {financeAppState.financeItemErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="finance-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="finance-form-container"
            >
              <Grid item xs={12} data-testid="finance-details-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.form.basicDetails') ||
                    'Basic Details'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-name-field">
                <ControlledTextField
                  control={control}
                  name="name"
                  id="finance-name"
                  label={translateText('finance.list.columns.name') || 'Name'}
                  errorMessage={errors.name?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-amount-field">
                <ControlledTextField
                  control={control}
                  name="amount"
                  id="finance-amount"
                  label={
                    translateText('finance.list.columns.amount') || 'Amount'
                  }
                  type="number"
                  errorMessage={errors.amount?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-currency-field">
                <ControlledSelectField
                  control={control}
                  name="currency"
                  id="finance-currency"
                  label={translateText('finance.list.currency') || 'Currency'}
                  itemList={getCurrencyOptions(translateText)}
                  errorMessage={errors.currency?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-date-field">
                <ControlledDateField
                  control={control}
                  name="date"
                  id="finance-date"
                  label={translateText('finance.list.columns.date') || 'Date'}
                  errorMessage={errors.date?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-due-date-field">
                <ControlledDateField
                  control={control}
                  name="dueDate"
                  id="finance-due-date"
                  label={translateText('finance.list.dueDate') || 'Due Date'}
                  errorMessage={errors.dueDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-type-field">
                <ControlledSelectField
                  control={control}
                  name="type"
                  id="finance-type"
                  label={translateText('finance.list.columns.type') || 'Type'}
                  itemList={getTransactionTypeOptions(translateText)}
                  errorMessage={errors.type?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-category-field">
                <ControlledSelectField
                  control={control}
                  name="category"
                  id="finance-category"
                  label={
                    translateText('finance.list.columns.category') || 'Category'
                  }
                  itemList={getCategoryOptions(translateText)}
                  errorMessage={errors.category?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-status-field">
                <ControlledSelectField
                  control={control}
                  name="status"
                  id="finance-status"
                  label={
                    translateText('finance.list.columns.status') || 'Status'
                  }
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="finance-payment-mode-field"
              >
                <ControlledSelectField
                  control={control}
                  name="paymentMode"
                  id="finance-payment-mode"
                  label={
                    translateText('finance.list.columns.paymentMode') ||
                    'Payment Mode'
                  }
                  itemList={getPaymentModeOptions(translateText)}
                  errorMessage={errors.paymentMode?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="finance-paid-amount-field">
                <ControlledTextField
                  control={control}
                  name="paidAmount"
                  id="finance-paid-amount"
                  label={
                    translateText('finance.list.paidAmount') || 'Paid Amount'
                  }
                  type="number"
                  errorMessage={errors.paidAmount?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="finance-academic-year-field"
              >
                <ControlledTextField
                  control={control}
                  name="academicYear"
                  id="finance-academic-year"
                  label={
                    translateText('finance.list.academicYear') ||
                    'Academic Year'
                  }
                  type="number"
                  errorMessage={errors.academicYear?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="finance-description-field">
                <ControlledTextField
                  control={control}
                  name="description"
                  id="finance-description"
                  label={
                    translateText('finance.list.description') || 'Description'
                  }
                  errorMessage={errors.description?.message}
                  required
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} data-testid="finance-tags-field">
                <ControlledTextField
                  control={control}
                  name="tags"
                  id="finance-tags"
                  label={translateText('finance.form.tags') || 'Tags'}
                  helperText={
                    translateText('finance.form.tagsHelper') ||
                    'Separate tags with commas'
                  }
                  errorMessage={errors.tags?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} data-testid="finance-documents-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('finance.list.documents') || 'Documents'}
                </Typography>
              </Grid>

              {(methods.watch('documents') || []).map(
                (_doc: any, index: number) => (
                  <Grid
                    item
                    xs={12}
                    key={index}
                    data-testid={`finance-document-${index}`}
                  >
                    <ControlledFileUploadField
                      control={control}
                      name={`documents.${index}.file_key`}
                      id={`finance-document-${index}`}
                      label={
                        translateText('documents.documentFile') ||
                        'Document File'
                      }
                      sectionTitle={`${translateText('documents.document') || 'Document'} ${index + 1}`}
                      titleName={`documents.${index}.name`}
                      descriptionName={`documents.${index}.description`}
                      statusName={`documents.${index}.status`}
                      verificationName={`documents.${index}.verificationDate`}
                    />
                  </Grid>
                )
              )}

              <Grid item xs={12} data-testid="finance-add-document-button">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    const currentDocs = methods.getValues('documents') || [];
                    methods.setValue('documents', [
                      ...currentDocs,
                      {
                        name: '',
                        document_type: '',
                        status: 'pending',
                        file_key: '',
                      },
                    ]);
                  }}
                  data-testid="add-document-button"
                >
                  {translateText('documents.addDocument') || 'Add Document'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditFinancialRecordView;
